import { Fragment, useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  ListGroup,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import {
  BsArrowRepeat,
  BsCalendarEvent,
  BsCameraVideo,
  BsClipboard,
  BsClipboardCheck,
  BsClockHistory,
  BsEnvelope,
  BsGear,
  BsHourglassSplit,
  BsPauseFill,
  BsPeople,
  BsPlayFill,
  BsPlus,
  BsSearch,
  BsPersonLinesFill,
  BsX,
} from "react-icons/bs";
import { useGetMeetingSchedulers } from "../../api/queries/useGetMeetingSchedulers";
import { useGetMeetingSchedulerGuests } from "../../api/queries/useGetMeetingSchedulerGuests";
import {
  useExcludeMeetingSchedulerGuest,
  useIncludeMeetingSchedulerGuest,
  usePauseMeetingScheduler,
  usePlayMeetingScheduler,
  useUpdateMeetingScheduler,
} from "../../api/mutations/useMeetingScheduler";
import { useFindUsers } from "../../api/queries/useFindUser";
import {
  MeetingScheduler,
  MeetingSchedulerEventTemplate,
  User,
} from "../../types/Types";
import {
  BUSINESS_MEMBERSHIP,
  EXECUTIVE_MEMBERSHIP,
  isYouthOrEarlyStageMembership,
} from "../../utils/membershipTier";
import AdminPanelHeader from "./AdminPanelHeader";
import MeetingSchedulerEventConfigModal from "./MeetingSchedulerEventConfigModal";

const RECURRENCE_OPTIONS = [
  { value: "Daily", label: "Daily" },
  { value: "Weekly", label: "Weekly" },
  { value: "BiWeekly", label: "Bi-Weekly" },
  { value: "Monthly", label: "Monthly" },
  { value: "Bimonthly", label: "Bi-Monthly" },
];

const DEFAULT_ORGANIZER_EMAIL = "tech@smbf.global";
/** Matches API GoogleMeetOptions.MeetingDurationMinutes default. */
const MEETING_DURATION_MINUTES = 60;

/** Segment mapping help text for the current forum only. */
function explorerInviteSegmentHint(membershipCategory?: string | null): string {
  if (isYouthOrEarlyStageMembership(membershipCategory)) {
    return "Early Stage Entrepreneur → Early Stage.";
  }
  if (
    membershipCategory?.trim().toLowerCase() === BUSINESS_MEMBERSHIP.toLowerCase()
  ) {
    return "Small to Medium → Business.";
  }
  if (
    membershipCategory?.trim().toLowerCase() === EXECUTIVE_MEMBERSHIP.toLowerCase()
  ) {
    return "Established/Executive → Executive.";
  }
  return "Explorers are matched to this forum by their segment.";
}

/** Other paid memberships that can be opted into for this scheduler card. */
function crossMembershipIncludeOptions(membershipCategory?: string | null): Array<{
  key: "includeEarlyStageMembers" | "includeBusinessMembers" | "includeExecutiveMembers";
  label: string;
}> {
  const options: Array<{
    key: "includeEarlyStageMembers" | "includeBusinessMembers" | "includeExecutiveMembers";
    label: string;
  }> = [];

  if (!isYouthOrEarlyStageMembership(membershipCategory)) {
    options.push({
      key: "includeEarlyStageMembers",
      label: "Include Early Stage members",
    });
  }
  if (
    membershipCategory?.trim().toLowerCase() !== BUSINESS_MEMBERSHIP.toLowerCase()
  ) {
    options.push({
      key: "includeBusinessMembers",
      label: "Include Business members",
    });
  }
  if (
    membershipCategory?.trim().toLowerCase() !== EXECUTIVE_MEMBERSHIP.toLowerCase()
  ) {
    options.push({
      key: "includeExecutiveMembers",
      label: "Include Executive members",
    });
  }

  return options;
}

function formatGuestResponseStatus(status?: string | null): string {
  if (!status) return "—";
  switch (status.toLowerCase()) {
    case "accepted":
      return "Accepted";
    case "declined":
      return "Declined";
    case "tentative":
      return "Tentative";
    case "needsaction":
      return "Needs action";
    default:
      return status;
  }
}

function inviteeKindBadgeClass(kind: string): string {
  switch (kind) {
    case "Member":
    case "Early Stage":
    case "Business":
    case "Executive":
      return "border border-primary text-primary bg-primary-subtle";
    case "Explorer":
      return "border border-info text-info bg-info-subtle";
    case "External":
      return "border border-secondary text-secondary bg-secondary-subtle";
    case "Organizer":
      return "border border-success text-success bg-success-subtle";
    default:
      return "border border-dark-subtle text-secondary bg-light";
  }
}

function InviteeNameWithKindTag({
  fullName,
  email,
  kind,
  struck = false,
}: {
  fullName?: string | null;
  email: string;
  kind: string;
  struck?: boolean;
}) {
  const name = (fullName || email).trim();
  return (
    <div className="min-w-0">
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span className={`fw-semibold text-truncate ${struck ? "text-decoration-line-through" : ""}`}>
          {name}
        </span>
        <span
          className={`badge rounded-pill fw-semibold ${inviteeKindBadgeClass(kind)}`}
          style={{ fontSize: "0.7rem" }}
        >
          {kind}
        </span>
      </div>
      <div className={`small text-muted text-truncate ${struck ? "text-decoration-line-through" : ""}`}>
        {email}
      </div>
    </div>
  );
}

function organizerEmail(value?: string | null): string {
  const trimmed = value?.trim();
  return trimmed || DEFAULT_ORGANIZER_EMAIL;
}

/** Prefer event booking-confirmation email when Create Event is on; else scheduler email. */
function hasInviteEmailConfigured(scheduler: MeetingScheduler): boolean {
  if (scheduler.createEvent) {
    try {
      const raw = scheduler.eventTemplateJson?.trim();
      if (raw) {
        const template = JSON.parse(raw) as {
          bookingConfirmationEmailSubject?: string | null;
          bookingConfirmationEmailBodyHtml?: string | null;
        };
        if (
          template.bookingConfirmationEmailSubject?.trim() &&
          template.bookingConfirmationEmailBodyHtml?.trim()
        ) {
          return true;
        }
      }
    } catch {
      // Fall through to scheduler email.
    }
  }
  return !!(scheduler.emailSubject?.trim() && scheduler.emailBodyHtml?.trim());
}

function toLocalInputValue(value?: string | null): string {
  if (!value) return "";
  // Prefer the wall-clock digits as stored (system local), avoid UTC shift.
  const match = value.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (match) return `${match[1]}T${match[2]}`;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ukWallClockToUtcMs(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
): number {
  let utcMs = Date.UTC(year, month - 1, day, hour, minute, second);
  for (let i = 0; i < 2; i += 1) {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date(utcMs));
    const g = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? "0");
    const asUtc = Date.UTC(g("year"), g("month") - 1, g("day"), g("hour"), g("minute"), g("second"));
    utcMs -= asUtc - utcMs;
  }
  return utcMs;
}

function parseWallClock(value?: string | null): Date | null {
  if (!value) return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (match) {
    return new Date(
      ukWallClockToUtcMs(
        Number(match[1]),
        Number(match[2]),
        Number(match[3]),
        Number(match[4]),
        Number(match[5]),
        Number(match[6] || "0"),
      ),
    );
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function ukTimeZoneLabel(): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    timeZoneName: "short",
  }).formatToParts(new Date());
  const shortName = parts.find((p) => p.type === "timeZoneName")?.value || "GMT";
  return `UK (${shortName})`;
}

function formatUkDateTime(d: Date, timeZone: string): string {
  return d.toLocaleString("en-GB", {
    timeZone,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDisplay(value?: string | null): string {
  if (!value) return "Not set";
  const match = value.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (match) {
    // Stored digits are already UK wall-clock — do not convert through the browser timezone.
    const d = new Date(`${match[1]}T${match[2]}:00Z`);
    if (!Number.isNaN(d.getTime())) {
      return formatUkDateTime(d, "UTC");
    }
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Not set";
  return formatUkDateTime(d, "Europe/London");
}

function getCountdownParts(ms: number): { days: number; hours: string; mins: string; secs: string } {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return { days, hours: pad(hours), mins: pad(mins), secs: pad(secs) };
}

function addRecurrence(date: Date, frequency: string, direction: 1 | -1): Date {
  const d = new Date(date.getTime());
  const f = (frequency || "Weekly").toLowerCase();
  switch (f) {
    case "daily":
      d.setDate(d.getDate() + 1 * direction);
      break;
    case "biweekly":
      d.setDate(d.getDate() + 14 * direction);
      break;
    case "monthly":
      d.setMonth(d.getMonth() + 1 * direction);
      break;
    case "bimonthly":
      d.setMonth(d.getMonth() + 2 * direction);
      break;
    case "weekly":
    default:
      d.setDate(d.getDate() + 7 * direction);
      break;
  }
  return d;
}

/**
 * UI-only: link stays active until the meeting window ends (start + 60 min).
 * After a scheduler run, firstMeetingDateTime is already the next occurrence, so when
 * that start is more than one meeting-length away we treat the URL as the previous cycle.
 */
function isMeetingLinkActive(scheduler: MeetingScheduler, nowMs: number): boolean {
  const url = scheduler.googleMeetUrl?.trim();
  if (!url) return false;

  const start = parseWallClock(scheduler.firstMeetingDateTime);
  if (!start) return true;

  const durationMs = MEETING_DURATION_MINUTES * 60_000;
  const msUntilStart = start.getTime() - nowMs;

  // Next occurrence already rolled forward — current URL belongs to the previous meeting.
  if (msUntilStart > durationMs) {
    const prevStart = addRecurrence(start, scheduler.recurrenceFrequency, -1);
    return nowMs < prevStart.getTime() + durationMs;
  }

  return nowMs < start.getTime() + durationMs;
}

/** Current vs next meeting windows for card display when a Meet link is still active. */
function resolveMeetingWindows(
  scheduler: MeetingScheduler,
  nowMs: number,
): { showCurrent: boolean; currentStart: Date | null; nextStart: Date | null } {
  const storedStart = parseWallClock(scheduler.firstMeetingDateTime);
  if (!isMeetingLinkActive(scheduler, nowMs) || !storedStart) {
    return { showCurrent: false, currentStart: null, nextStart: storedStart };
  }

  const durationMs = MEETING_DURATION_MINUTES * 60_000;
  const msUntilStart = storedStart.getTime() - nowMs;

  if (msUntilStart > durationMs) {
    return {
      showCurrent: true,
      currentStart: addRecurrence(storedStart, scheduler.recurrenceFrequency, -1),
      nextStart: storedStart,
    };
  }

  return {
    showCurrent: true,
    currentStart: storedStart,
    nextStart: addRecurrence(storedStart, scheduler.recurrenceFrequency, 1),
  };
}

function formatDateDisplay(date: Date | null): string {
  if (!date || Number.isNaN(date.getTime())) return "Not set";
  return formatUkDateTime(date, "Europe/London");
}

/** Send exact UK wall-clock digits (no browser-timezone conversion). */
function toApiDateTime(localValue: string): string | null {
  if (!localValue) return null;
  return localValue.length === 16 ? `${localValue}:00` : localValue;
}

function recurrenceLabel(value?: string | null): string {
  return (
    RECURRENCE_OPTIONS.find((o) => o.value === value)?.label ||
    value ||
    "Not set"
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseEmails(raw: string): string[] {
  const parts = raw.split(/[,;\n\r\t ]+/).map((p) => p.trim()).filter(Boolean);
  const seen = new Set<string>();
  const result: string[] = [];
  for (const part of parts) {
    const key = part.toLowerCase();
    if (!EMAIL_RE.test(part) || seen.has(key)) continue;
    seen.add(key);
    result.push(part);
  }
  return result;
}

function belongsToSchedulerCategory(
  membershipName?: string | null,
  schedulerCategory?: string | null,
): boolean {
  const member = (membershipName ?? "").trim();
  const category = (schedulerCategory ?? "").trim();
  if (!member || !category) return false;
  if (isYouthOrEarlyStageMembership(member) && isYouthOrEarlyStageMembership(category)) {
    return true;
  }
  return member.toLowerCase() === category.toLowerCase();
}

function joinExternalEmails(emails: string[]): string | null {
  return emails.length ? emails.join(", ") : null;
}

function ExternalAttendeesField({
  schedulerCategory,
  emails,
  onChange,
}: {
  schedulerCategory?: string;
  emails: string[];
  onChange: (emails: string[]) => void;
}) {
  const [input, setInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(input.trim()), 300);
    return () => window.clearTimeout(id);
  }, [input]);

  const searchEnabled = debouncedSearch.length >= 2 && !/[,;]/.test(debouncedSearch);
  const { data: userResponse, isFetching } = useFindUsers({
    enabled: searchEnabled,
    pageSize: 8,
    page: 1,
    searchTerm: searchEnabled ? debouncedSearch : undefined,
    userType: "members",
  });

  const suggestions = useMemo(() => {
    const users = userResponse?.users?.data ?? [];
    const existing = new Set(emails.map((e) => e.toLowerCase()));
    return users.filter((user: User) => {
      const email = user.email?.trim();
      if (!email || existing.has(email.toLowerCase())) return false;
      return !belongsToSchedulerCategory(user.membershipName, schedulerCategory);
    });
  }, [emails, schedulerCategory, userResponse]);

  const addEmails = (incoming: string[]) => {
    if (!incoming.length) return;
    const existing = new Set(emails.map((e) => e.toLowerCase()));
    const next = [...emails];
    for (const email of incoming) {
      const key = email.toLowerCase();
      if (existing.has(key)) continue;
      existing.add(key);
      next.push(email);
    }
    if (next.length !== emails.length) onChange(next);
  };

  const commitInput = () => {
    const parsed = parseEmails(input);
    if (!parsed.length) return false;
    addEmails(parsed);
    setInput("");
    setShowSuggestions(false);
    return true;
  };

  const handleInputChange = (value: string) => {
    if (/[,;\n]/.test(value)) {
      const parts = value.split(/[,;\n]+/);
      const last = parts.pop() ?? "";
      const complete = parseEmails(parts.join(","));
      if (complete.length) addEmails(complete);
      setInput(last.replace(/^\s+/, ""));
      return;
    }
    setInput(value);
    setShowSuggestions(true);
  };

  const handleSelectMember = (user: User) => {
    const email = user.email?.trim();
    if (!email) return;
    addEmails([email]);
    setInput("");
    setShowSuggestions(false);
  };

  return (
    <Form.Group className="mb-0">
      <div className="position-relative">
        <InputGroup>
          <InputGroup.Text className="bg-white">
            <BsSearch className="text-muted" size={14} />
          </InputGroup.Text>
          <Form.Control
            type="text"
            value={input}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => window.setTimeout(() => setShowSuggestions(false), 180)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitInput();
              }
            }}
            placeholder="Search other membership members, or paste emails"
            autoComplete="off"
          />
          <Button
            variant="outline-primary"
            onMouseDown={(e) => e.preventDefault()}
            onClick={commitInput}
            disabled={!parseEmails(input).length}
          >
            <BsPlus size={18} />
            Add
          </Button>
        </InputGroup>

        {showSuggestions && searchEnabled && (
          <ListGroup
            className="position-absolute w-100 shadow-sm mt-1"
            style={{ zIndex: 20, maxHeight: 220, overflowY: "auto" }}
          >
            {isFetching && (
              <ListGroup.Item className="small text-muted">Searching members…</ListGroup.Item>
            )}
            {!isFetching && suggestions.length === 0 && (
              <ListGroup.Item className="small text-muted">
                No other-membership members found. Add a custom email and press Enter.
              </ListGroup.Item>
            )}
            {suggestions.map((user) => (
              <ListGroup.Item
                key={user.id}
                action
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelectMember(user)}
                className="py-2"
              >
                <div className="fw-semibold">
                  {`${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email}
                </div>
                <div className="small text-muted">
                  {user.email}
                  {user.membershipName ? ` · ${user.membershipName}` : ""}
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </div>

      {emails.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mt-3">
          {emails.map((email) => (
            <Badge
              key={email}
              bg="light"
              text="dark"
              className="d-inline-flex align-items-center gap-1 fw-normal border py-2 px-2"
            >
              {email}
              <button
                type="button"
                className="btn btn-link p-0 lh-1 text-secondary"
                aria-label={`Remove ${email}`}
                onClick={() => onChange(emails.filter((item) => item !== email))}
              >
                <BsX size={16} />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </Form.Group>
  );
}

const ManageMeetings = () => {
  const { data: schedulers = [], isLoading, isError, error } = useGetMeetingSchedulers();
  const updateMutation = useUpdateMeetingScheduler();
  const playMutation = usePlayMeetingScheduler();
  const pauseMutation = usePauseMeetingScheduler();
  const excludeGuestMutation = useExcludeMeetingSchedulerGuest();
  const includeGuestMutation = useIncludeMeetingSchedulerGuest();

  const [selected, setSelected] = useState<MeetingScheduler | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [guestsView, setGuestsView] = useState<"current" | "next" | null>(null);
  const [guestsSearch, setGuestsSearch] = useState("");
  const [firstMeeting, setFirstMeeting] = useState("");
  const [nextMeeting, setNextMeeting] = useState("");
  const [recurrence, setRecurrence] = useState("Weekly");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBodyHtml, setEmailBodyHtml] = useState("");
  const [externalEmails, setExternalEmails] = useState<string[]>([]);
  const [includeExplorers, setIncludeExplorers] = useState(true);
  const [includeEarlyStageMembers, setIncludeEarlyStageMembers] = useState(false);
  const [includeBusinessMembers, setIncludeBusinessMembers] = useState(false);
  const [includeExecutiveMembers, setIncludeExecutiveMembers] = useState(false);
  const [createEvent, setCreateEvent] = useState(false);
  const [eventTemplateJson, setEventTemplateJson] = useState<string | null>(null);
  const [showEventConfig, setShowEventConfig] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const {
    data: guestsData,
    isLoading: guestsLoading,
    isError: guestsIsError,
    error: guestsError,
    refetch: refetchGuests,
  } = useGetMeetingSchedulerGuests(selected?.id, { enabled: guestsView != null });

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!selected) return;
    setFirstMeeting(toLocalInputValue(selected.firstMeetingDateTime));
    setNextMeeting(toLocalInputValue(selected.nextSchedulerDateTime));
    setRecurrence(selected.recurrenceFrequency || "Weekly");
    setEmailSubject(selected.emailSubject || "");
    setEmailBodyHtml(selected.emailBodyHtml || "");
    setExternalEmails(parseEmails(selected.externalAttendeeEmails || ""));
    setIncludeExplorers(selected.includeExplorers !== false);
    setIncludeEarlyStageMembers(!!selected.includeEarlyStageMembers);
    setIncludeBusinessMembers(!!selected.includeBusinessMembers);
    setIncludeExecutiveMembers(!!selected.includeExecutiveMembers);
    setCreateEvent(!!selected.createEvent);
    setEventTemplateJson(selected.eventTemplateJson ?? null);
    setActionError(null);
  }, [selected]);

  const openConfig = (scheduler: MeetingScheduler) => {
    setSelected(scheduler);
    setShowConfig(true);
  };

  const closeConfig = () => {
    setShowConfig(false);
    setShowEventConfig(false);
    setGuestsView(null);
    setSelected(null);
    setActionError(null);
  };

  const buildUpdatePayload = (overrides?: {
    createEvent?: boolean;
    eventTemplateJson?: string | null;
    emails?: string[];
    includeExplorers?: boolean;
    includeEarlyStageMembers?: boolean;
    includeBusinessMembers?: boolean;
    includeExecutiveMembers?: boolean;
  }) => ({
    googleAccountEmail: organizerEmail(selected?.googleAccountEmail),
    firstMeetingDateTime: toApiDateTime(firstMeeting),
    nextSchedulerDateTime: toApiDateTime(nextMeeting),
    recurrenceFrequency: recurrence,
    emailSubject: emailSubject.trim() || null,
    emailBodyHtml: emailBodyHtml.trim() || null,
    externalAttendeeEmails: joinExternalEmails(overrides?.emails ?? externalEmails),
    includeExplorers: overrides?.includeExplorers ?? includeExplorers,
    includeEarlyStageMembers:
      overrides?.includeEarlyStageMembers ?? includeEarlyStageMembers,
    includeBusinessMembers:
      overrides?.includeBusinessMembers ?? includeBusinessMembers,
    includeExecutiveMembers:
      overrides?.includeExecutiveMembers ?? includeExecutiveMembers,
    createEvent: overrides?.createEvent ?? createEvent,
    eventTemplateJson:
      overrides?.eventTemplateJson !== undefined
        ? overrides.eventTemplateJson
        : eventTemplateJson,
  });

  const openCurrentGuests = () => {
    if (!selected || !isMeetingLinkActive(selected, nowMs)) return;
    setGuestsSearch("");
    setGuestsView("current");
  };
  const openNextGuests = () => {
    setGuestsSearch("");
    setGuestsView("next");
  };
  const closeGuests = () => {
    setGuestsView(null);
    setGuestsSearch("");
  };

  const canViewCurrentMeetingGuests =
    !!selected &&
    selected.status === "Running" &&
    isMeetingLinkActive(selected, nowMs);

  const guestsQuery = guestsSearch.trim().toLowerCase();
  const filteredNextInvitees = useMemo(() => {
    const list = guestsData?.nextInvitees ?? [];
    if (!guestsQuery) return list;
    return list.filter((invitee) => {
      const name = (invitee.fullName || "").toLowerCase();
      const email = (invitee.email || "").toLowerCase();
      return name.includes(guestsQuery) || email.includes(guestsQuery);
    });
  }, [guestsData?.nextInvitees, guestsQuery]);

  const filteredExcludedInvitees = useMemo(() => {
    const list = guestsData?.excludedInvitees ?? [];
    if (!guestsQuery) return list;
    return list.filter((invitee) => {
      const name = (invitee.fullName || "").toLowerCase();
      const email = (invitee.email || "").toLowerCase();
      return name.includes(guestsQuery) || email.includes(guestsQuery);
    });
  }, [guestsData?.excludedInvitees, guestsQuery]);

  const filteredCurrentGuests = useMemo(() => {
    const list = guestsData?.currentMeeting?.guests ?? [];
    if (!guestsQuery) return list;
    return list.filter((guest) => {
      const name = (guest.displayName || "").toLowerCase();
      const email = (guest.email || "").toLowerCase();
      return name.includes(guestsQuery) || email.includes(guestsQuery);
    });
  }, [guestsData?.currentMeeting?.guests, guestsQuery]);

  const guestActionBusyEmail =
    excludeGuestMutation.isPending || includeGuestMutation.isPending
      ? (excludeGuestMutation.variables?.email || includeGuestMutation.variables?.email || null)
      : null;

  const handleExcludeGuest = async (email: string) => {
    if (!selected?.id) return;
    try {
      await excludeGuestMutation.mutateAsync({ id: selected.id, email });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: string } })?.response?.data ||
        "Failed to exclude guest.";
      setActionError(typeof msg === "string" ? msg : "Failed to exclude guest.");
    }
  };

  const handleIncludeGuest = async (email: string) => {
    if (!selected?.id) return;
    try {
      await includeGuestMutation.mutateAsync({ id: selected.id, email });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: string } })?.response?.data ||
        "Failed to include guest.";
      setActionError(typeof msg === "string" ? msg : "Failed to include guest.");
    }
  };

  const persistNextGuestSettings = async (opts: {
    emails?: string[];
    includeExplorers?: boolean;
    includeEarlyStageMembers?: boolean;
    includeBusinessMembers?: boolean;
    includeExecutiveMembers?: boolean;
  }) => {
    if (!selected?.id) return;
    const nextEmails = opts.emails ?? externalEmails;
    const nextIncludeExplorers = opts.includeExplorers ?? includeExplorers;
    const nextIncludeEarlyStage =
      opts.includeEarlyStageMembers ?? includeEarlyStageMembers;
    const nextIncludeBusiness =
      opts.includeBusinessMembers ?? includeBusinessMembers;
    const nextIncludeExecutive =
      opts.includeExecutiveMembers ?? includeExecutiveMembers;
    setExternalEmails(nextEmails);
    setIncludeExplorers(nextIncludeExplorers);
    setIncludeEarlyStageMembers(nextIncludeEarlyStage);
    setIncludeBusinessMembers(nextIncludeBusiness);
    setIncludeExecutiveMembers(nextIncludeExecutive);
    setActionError(null);
    try {
      const updated = await updateMutation.mutateAsync({
        id: selected.id,
        payload: buildUpdatePayload({
          emails: nextEmails,
          includeExplorers: nextIncludeExplorers,
          includeEarlyStageMembers: nextIncludeEarlyStage,
          includeBusinessMembers: nextIncludeBusiness,
          includeExecutiveMembers: nextIncludeExecutive,
        }),
      });
      setSelected(updated);
      await refetchGuests();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: string } })?.response?.data ||
        "Failed to update guest settings.";
      setActionError(typeof msg === "string" ? msg : "Failed to update guest settings.");
    }
  };

  useEffect(() => {
    if (guestsView !== "current" || !selected) return;
    if (selected.status !== "Running" || !isMeetingLinkActive(selected, nowMs)) {
      setGuestsView(null);
    }
  }, [guestsView, selected, nowMs]);

  const handleSave = async () => {
    if (!selected) return;
    setActionError(null);

    if (createEvent) {
      let templateOk = false;
      try {
        const parsed = eventTemplateJson
          ? (JSON.parse(eventTemplateJson) as MeetingSchedulerEventTemplate)
          : null;
        templateOk =
          !!parsed?.name?.trim() && !!parsed?.description?.trim();
      } catch {
        templateOk = false;
      }
      if (!templateOk) {
        setActionError(
          "Create Event is enabled. Click Configure Event and save a valid online event template first.",
        );
        return;
      }
    }

    try {
      await updateMutation.mutateAsync({
        id: selected.id,
        payload: buildUpdatePayload(),
      });
      closeConfig();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: string } })?.response?.data ||
        "Failed to save configuration.";
      setActionError(typeof msg === "string" ? msg : "Failed to save configuration.");
    }
  };

  const handleCreateEventToggle = (checked: boolean) => {
    setCreateEvent(checked);
    if (!checked) {
      setShowEventConfig(false);
    }
  };

  const handleSaveEventTemplate = async (template: MeetingSchedulerEventTemplate) => {
    if (!selected?.id) return;
    const json = JSON.stringify(template);
    setActionError(null);
    try {
      const updated = await updateMutation.mutateAsync({
        id: selected.id,
        payload: buildUpdatePayload({
          createEvent: true,
          eventTemplateJson: json,
        }),
      });
      const savedJson = updated.eventTemplateJson?.trim() || json;
      setCreateEvent(true);
      setEventTemplateJson(savedJson);
      // Keep local create-event fields even if the response is briefly stale.
      setSelected({
        ...updated,
        createEvent: true,
        eventTemplateJson: savedJson,
      });
      setShowEventConfig(false);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: string } })?.response?.data ||
        "Failed to save event configuration.";
      setActionError(
        typeof msg === "string" ? msg : "Failed to save event configuration.",
      );
    }
  };

  const handleTogglePlayPause = async (scheduler: MeetingScheduler) => {
    const isRunning = scheduler.status === "Running";
    setBusyId(scheduler.id);
    setActionError(null);

    if (!isRunning) {
      if (!scheduler.firstMeetingDateTime || !scheduler.nextSchedulerDateTime) {
        setActionError("Configure Meeting Date / Time and Scheduler Run Date / Time before starting.");
        setBusyId(null);
        return;
      }
      if (!hasInviteEmailConfigured(scheduler)) {
        setActionError(
          "Configure Email Subject and Email Body on the scheduler, or enable Create Event with booking confirmation email, before starting.",
        );
        setBusyId(null);
        return;
      }
    }

    try {
      if (isRunning) {
        await pauseMutation.mutateAsync(scheduler.id);
      } else {
        if (!scheduler.googleAccountEmail?.trim()) {
          await updateMutation.mutateAsync({
            id: scheduler.id,
            payload: {
              googleAccountEmail: DEFAULT_ORGANIZER_EMAIL,
              firstMeetingDateTime: scheduler.firstMeetingDateTime ?? null,
              nextSchedulerDateTime: scheduler.nextSchedulerDateTime ?? null,
              recurrenceFrequency: scheduler.recurrenceFrequency || "Weekly",
              emailSubject: scheduler.emailSubject ?? null,
              emailBodyHtml: scheduler.emailBodyHtml ?? null,
              externalAttendeeEmails: scheduler.externalAttendeeEmails ?? null,
              includeExplorers: scheduler.includeExplorers !== false,
              includeEarlyStageMembers: !!scheduler.includeEarlyStageMembers,
              includeBusinessMembers: !!scheduler.includeBusinessMembers,
              includeExecutiveMembers: !!scheduler.includeExecutiveMembers,
              createEvent: !!scheduler.createEvent,
              eventTemplateJson: scheduler.eventTemplateJson ?? null,
            },
          });
        }
        await playMutation.mutateAsync(scheduler.id);
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: string } })?.response?.data ||
        (isRunning ? "Failed to pause scheduler." : "Failed to start scheduler.");
      setActionError(
        typeof msg === "string"
          ? msg
          : isRunning
            ? "Failed to pause scheduler."
            : "Failed to start scheduler."
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleCopyMeetLink = async (scheduler: MeetingScheduler) => {
    const url = scheduler.googleMeetUrl?.trim();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(scheduler.id);
      window.setTimeout(() => {
        setCopiedId((current) => (current === scheduler.id ? null : current));
      }, 2000);
    } catch {
      setActionError("Failed to copy meeting link.");
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="alert alert-danger">
        Failed to load meeting schedulers.
        {error instanceof Error ? ` ${error.message}` : ""}
      </div>
    );
  }

  return (
    <div className="w-100">
      <AdminPanelHeader
        icon={<BsCalendarEvent size={22} />}
        title="Meeting Schedulers"
        description="Automatic Google Meet creation for each membership category"
      />

      {actionError && !showConfig && (
        <div className="alert alert-danger py-2">{actionError}</div>
      )}

      <Row className="g-4">
        {schedulers.map((scheduler) => {
          const isRunning = scheduler.status === "Running";
          const isBusy = busyId === scheduler.id;
          return (
            <Col key={scheduler.id} md={4}>
              <Card className="h-100 shadow border-0 overflow-hidden">
                <Card.Header className={`${isRunning ? "bg-project-gradient" : "bg-secondary"} text-white border-0 py-3`}>
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <span className="bg-white bg-opacity-25 rounded-circle p-2 d-inline-flex">
                        <BsPeople size={18} />
                      </span>
                      <div>
                        <div className="fw-bold">{scheduler.membershipCategory}</div>
                        <div className="small text-white-50 d-flex align-items-center gap-1">
                          <BsArrowRepeat size={12} />
                          {recurrenceLabel(scheduler.recurrenceFrequency)}
                        </div>
                      </div>
                    </div>
                    <span
                      className={`badge rounded-pill ${
                        isRunning ? "text-bg-success" : "text-bg-light text-dark"
                      }`}
                    >
                      {scheduler.status}
                    </span>
                  </div>
                </Card.Header>

                <Card.Body
                  className="bg-white"
                  style={!isRunning ? { filter: "grayscale(0.4)", opacity: 0.75 } : undefined}
                >
                  {(() => {
                    const url = scheduler.googleMeetUrl?.trim() || "";
                    const { showCurrent, currentStart, nextStart } = resolveMeetingWindows(
                      scheduler,
                      nowMs,
                    );
                    const nextRun = parseWallClock(scheduler.nextSchedulerDateTime);
                    const remainingMs = nextRun ? nextRun.getTime() - nowMs : 0;
                    const waitingForNext = !!nextRun && remainingMs > 0;
                    const nextMeetingDisplay = showCurrent
                      ? formatDateDisplay(nextStart)
                      : formatDisplay(scheduler.firstMeetingDateTime);

                    const sectionTitle = (label: string) => (
                      <div
                        className="d-flex align-items-center gap-2 mb-2 px-2 py-1 rounded-2"
                        style={{
                          background: "linear-gradient(90deg, rgba(7,92,203,0.12) 0%, rgba(7,92,203,0.02) 100%)",
                          borderLeft: "3px solid #075ccb",
                        }}
                      >
                        <span className="fw-bold text-dark" style={{ fontSize: "0.85rem", letterSpacing: "0.02em" }}>
                          {label}
                        </span>
                      </div>
                    );

                    const renderCurrentLink = () => {
                      if (showCurrent && url) {
                        return (
                          <div className="d-flex align-items-center gap-3 p-2 rounded-3 bg-primary-subtle border border-primary-subtle">
                            <span className="rounded-circle bg-project-gradient text-white p-2 d-inline-flex flex-shrink-0">
                              <BsCameraVideo size={16} />
                            </span>
                            <div className="min-w-0 flex-grow-1">
                              <div className="small text-primary fw-semibold">Meeting link</div>
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="fw-semibold text-break small"
                              >
                                {url}
                              </a>
                            </div>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              title={copiedId === scheduler.id ? "Copied" : "Copy link"}
                              aria-label="Copy meeting link"
                              className="flex-shrink-0 d-inline-flex align-items-center justify-content-center"
                              onClick={() => handleCopyMeetLink(scheduler)}
                            >
                              {copiedId === scheduler.id ? (
                                <BsClipboardCheck size={15} />
                              ) : (
                                <BsClipboard size={15} />
                              )}
                            </Button>
                          </div>
                        );
                      }

                      if (!isRunning) {
                        return (
                          <div className="d-flex align-items-center gap-3 p-2 rounded-3 bg-light border">
                            <span className="rounded-circle bg-secondary text-white p-2 d-inline-flex flex-shrink-0">
                              <BsPauseFill size={16} />
                            </span>
                            <div className="min-w-0 flex-grow-1">
                              <div className="small text-secondary fw-semibold">Meeting link</div>
                              <div className="fw-semibold text-muted">Paused</div>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div
                          className="position-relative overflow-hidden rounded-3 border"
                          style={{
                            minHeight: 76,
                            borderColor: "rgba(7, 92, 203, 0.22)",
                            background:
                              "linear-gradient(135deg, rgba(35,102,205,0.06) 0%, rgba(58,151,164,0.08) 100%)",
                          }}
                        >
                          <div
                            className="d-flex align-items-center gap-3 p-2"
                            style={{
                              filter: "blur(4px)",
                              opacity: 0.35,
                              pointerEvents: "none",
                              userSelect: "none",
                            }}
                            aria-hidden="true"
                          >
                            <span className="rounded-circle bg-project-gradient text-white p-2 d-inline-flex flex-shrink-0">
                              <BsCameraVideo size={16} />
                            </span>
                            <div className="min-w-0 flex-grow-1">
                              <div className="small text-primary fw-semibold">Meeting link</div>
                              <div className="fw-semibold text-break small text-muted">
                                {url || "https://meet.google.com/abc-defg-hij"}
                              </div>
                            </div>
                          </div>
                          <div
                            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center px-3 py-2"
                            style={{
                              backdropFilter: "blur(7px)",
                              WebkitBackdropFilter: "blur(7px)",
                              background: "rgba(255,255,255,0.55)",
                            }}
                          >
                            <div className="d-flex align-items-center gap-3 w-100">
                              <span
                                className="rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 text-white"
                                style={{
                                  width: 36,
                                  height: 36,
                                  background:
                                    "linear-gradient(165deg, #031b3d 0%, #082f6f 55%, #02152f 100%)",
                                  boxShadow: "0 2px 8px rgba(35,102,205,0.25)",
                                }}
                              >
                                <BsHourglassSplit size={15} />
                              </span>
                              <div className="min-w-0 flex-grow-1">
                                <div
                                  className="small fw-semibold mb-0"
                                  style={{ color: "#075ccb", letterSpacing: "0.01em" }}
                                >
                                  {url ? "Link expired" : "No meeting link"}
                                </div>
                                <div className="small text-muted" style={{ lineHeight: 1.2 }}>
                                  {waitingForNext
                                    ? "Next scheduler in"
                                    : "Waiting for next schedule"}
                                </div>
                              </div>
                              {waitingForNext && (() => {
                                const { days, hours, mins, secs } = getCountdownParts(remainingMs);
                                const boxStyle: CSSProperties = {
                                  background: "rgba(35,102,205,0.1)",
                                  border: "1px solid rgba(35,102,205,0.18)",
                                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                  letterSpacing: "0.03em",
                                  fontSize: "0.85rem",
                                  color: "#1f4f9c",
                                  lineHeight: 1.2,
                                  minWidth: 28,
                                };
                                const sepStyle: CSSProperties = {
                                  color: "#1f4f9c",
                                  fontWeight: 700,
                                  fontSize: "0.85rem",
                                  lineHeight: 1,
                                  opacity: 0.7,
                                };
                                const segments = days > 0
                                  ? [`${days}d`, hours, mins, secs]
                                  : [hours, mins, secs];
                                return (
                                  <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                    {segments.map((value, i) => (
                                      <Fragment key={i}>
                                        {i > 0 && <span style={sepStyle}>:</span>}
                                        <div
                                          className="px-1 py-1 rounded-2 text-center fw-bold"
                                          style={boxStyle}
                                        >
                                          {value}
                                        </div>
                                      </Fragment>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      );
                    };

                    return (
                      <>
                        <div className="mb-3 pb-3 border-bottom">
                          {sectionTitle("Current meeting")}
                          {showCurrent && currentStart ? (
                            <div className="d-flex align-items-center gap-3 mb-2 p-2 rounded-3 bg-primary-subtle border border-primary-subtle">
                              <span className="rounded-circle bg-project-gradient text-white p-2 d-inline-flex flex-shrink-0">
                                <BsCalendarEvent size={16} />
                              </span>
                              <div className="min-w-0">
                                <div className="small text-primary fw-semibold">
                                  Meeting date / time
                                </div>
                                <div className="fw-semibold text-dark">
                                  {formatDateDisplay(currentStart)}{" "}
                                  <span className="small fw-normal text-muted">
                                    {ukTimeZoneLabel()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center gap-3 mb-2 p-2 rounded-3 bg-light border">
                              <span className="rounded-circle bg-secondary text-white p-2 d-inline-flex flex-shrink-0">
                                <BsCalendarEvent size={16} />
                              </span>
                              <div className="min-w-0">
                                <div className="small text-secondary fw-semibold">
                                  Meeting date / time
                                </div>
                                <div className="fw-semibold text-muted">No active meeting</div>
                              </div>
                            </div>
                          )}
                          {renderCurrentLink()}
                        </div>

                        <div className="mb-3 pb-3 border-bottom">
                          {sectionTitle("Next meeting")}
                          <div className="d-flex align-items-center gap-3 mb-2 p-2 rounded-3 bg-info-subtle border border-info-subtle">
                            <span className="rounded-circle bg-info text-white p-2 d-inline-flex flex-shrink-0">
                              <BsCalendarEvent size={16} />
                            </span>
                            <div className="min-w-0">
                              <div className="small text-info fw-semibold">Meeting date / time</div>
                              <div className="fw-semibold text-dark">
                                {nextMeetingDisplay}{" "}
                                <span className="small fw-normal text-muted">
                                  {ukTimeZoneLabel()}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-3 p-2 rounded-3 bg-light border">
                            <span className="rounded-circle bg-secondary text-white p-2 d-inline-flex flex-shrink-0">
                              <BsClockHistory size={16} />
                            </span>
                            <div className="min-w-0">
                              <div className="small fw-semibold text-secondary">
                                Scheduler run date / time
                              </div>
                              <div className="fw-semibold text-dark">
                                {formatDisplay(scheduler.nextSchedulerDateTime)}{" "}
                                <span className="small fw-normal text-muted">
                                  {ukTimeZoneLabel()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-3 p-2 rounded-3 bg-success-subtle border border-success-subtle">
                          <span className="rounded-circle bg-success text-white p-2 d-inline-flex flex-shrink-0">
                            <BsEnvelope size={16} />
                          </span>
                          <div className="min-w-0">
                            <div className="small text-success fw-semibold">Organizer</div>
                            <div className="fw-semibold text-dark text-break">
                              {organizerEmail(scheduler.googleAccountEmail)}
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </Card.Body>

                <Card.Footer className="bg-light border-0 d-flex gap-2 py-3">
                  <Button
                    size="sm"
                    variant="primary"
                    title="Configure"
                    aria-label="Configure"
                    className="flex-fill d-inline-flex align-items-center justify-content-center gap-2 bg-project-gradient border-0"
                    onClick={() => openConfig(scheduler)}
                  >
                    <BsGear size={15} />
                    Configure
                  </Button>
                  <Button
                    size="sm"
                    variant={isRunning ? "secondary" : "success"}
                    title={isRunning ? "Pause" : "Play"}
                    aria-label={isRunning ? "Pause" : "Play"}
                    disabled={isBusy}
                    className="flex-fill d-inline-flex align-items-center justify-content-center gap-2"
                    onClick={() => handleTogglePlayPause(scheduler)}
                  >
                    {isBusy ? (
                      <Spinner animation="border" size="sm" />
                    ) : isRunning ? (
                      <>
                        <BsPauseFill size={15} />
                        Pause
                      </>
                    ) : (
                      <>
                        <BsPlayFill size={15} />
                        Play
                      </>
                    )}
                  </Button>
                </Card.Footer>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Modal
        show={showConfig}
        onHide={closeConfig}
        centered
        size="lg"
        backdrop="static"
        scrollable
        dialogClassName="meeting-scheduler-modal"
      >
        <Modal.Header closeButton closeVariant="white" className="bg-project-gradient text-white border-0">
          <Modal.Title className="fs-5 d-flex align-items-center gap-2 text-white">
            <BsGear size={20} />
            {selected?.membershipCategory || "Scheduler"} — Configuration
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light" style={{ height: "60vh", overflowY: "auto" }}>
          {actionError && <div className="alert alert-danger py-2 mb-3">{actionError}</div>}

          <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
            <div className="d-flex flex-wrap gap-2">
              {canViewCurrentMeetingGuests && (
                <Button
                  size="sm"
                  variant="outline-primary"
                  className="d-inline-flex align-items-center gap-2"
                  onClick={openCurrentGuests}
                  disabled={!selected?.id}
                >
                  <BsPersonLinesFill size={14} />
                  View current meeting guests
                </Button>
              )}
              <Button
                size="sm"
                variant="outline-primary"
                className="d-inline-flex align-items-center gap-2"
                onClick={openNextGuests}
                disabled={!selected?.id}
              >
                <BsPeople size={14} />
                Manage next meeting guests
              </Button>
            </div>
            <span
              className={`badge rounded-pill ${
                selected?.status === "Running" ? "text-bg-success" : "text-bg-secondary"
              }`}
            >
              {selected?.status || "Paused"}
            </span>
          </div>

          <Form>
            <Card className="mb-3 border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom py-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="rounded-circle bg-project-gradient text-white p-2 d-inline-flex">
                    <BsCalendarEvent size={14} />
                  </span>
                  <div className="fw-semibold text-dark">Schedule settings</div>
                </div>
              </Card.Header>
              <Card.Body className="bg-white">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold d-flex align-items-center gap-2">
                    <BsEnvelope className="text-primary" />
                    Organizer email
                  </Form.Label>
                  <div className="d-flex align-items-center gap-2 px-3 py-2 rounded-2 bg-light border">
                    <BsEnvelope className="text-muted flex-shrink-0" size={14} />
                    <span className="text-break">
                      {organizerEmail(selected?.googleAccountEmail)}
                    </span>
                  </div>
                </Form.Group>

                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold d-flex align-items-center gap-2">
                        <BsCalendarEvent className="text-info" />
                        Meeting date / time
                        <span className="small fw-normal text-muted">{ukTimeZoneLabel()}</span>
                      </Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={firstMeeting}
                        onChange={(e) => {
                          setFirstMeeting(e.target.value);
                          if (!nextMeeting) setNextMeeting(e.target.value);
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold d-flex align-items-center gap-2">
                        <BsClockHistory className="text-secondary" />
                        Scheduler run date / time
                        <span className="small fw-normal text-muted">{ukTimeZoneLabel()}</span>
                      </Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={nextMeeting}
                        onChange={(e) => setNextMeeting(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-0">
                  <Form.Label className="fw-semibold d-flex align-items-center gap-2">
                    <BsArrowRepeat className="text-success" />
                    Recurrence frequency
                  </Form.Label>
                  <Form.Select
                    value={recurrence}
                    onChange={(e) => setRecurrence(e.target.value)}
                  >
                    {RECURRENCE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className="mb-3 border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom py-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="rounded-circle bg-success text-white p-2 d-inline-flex">
                    <BsEnvelope size={14} />
                  </span>
                  <div className="fw-semibold text-dark">Invitation email</div>
                </div>
              </Card.Header>
              <Card.Body className="bg-white">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Email subject</Form.Label>
                  <Form.Control
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="{Membership Category} Meeting Invitation"
                  />
                </Form.Group>

                <Form.Group className="mb-0">
                  <Form.Label className="fw-semibold">Email body</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={10}
                    value={emailBodyHtml}
                    onChange={(e) => setEmailBodyHtml(e.target.value)}
                    placeholder={
                      "Hi {First Name},\n\n" +
                      "You are invited to the {Membership Category} meeting on {Meeting Date Time}.\n\n" +
                      "Join here: {Google Meet Url}"
                    }
                  />
                  <Form.Text className="text-muted">
                    Required before Play. Tokens: {"{First Name}"}, {"{Membership Category}"},{" "}
                    {"{Meeting Date Time}"}, {"{Google Meet Url}"}.
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className="mb-0 border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom py-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="rounded-circle bg-info text-white p-2 d-inline-flex">
                    <BsCalendarEvent size={14} />
                  </span>
                  <div className="fw-semibold text-dark">Connect event</div>
                </div>
              </Card.Header>
              <Card.Body className="bg-white">
                <Form.Check
                  type="checkbox"
                  id="meeting-scheduler-create-event"
                  className="fw-semibold"
                  label="Create Event"
                  checked={createEvent}
                  onChange={(e) => handleCreateEventToggle(e.target.checked)}
                />
                <Form.Text className="text-muted d-block mb-2">
                  When enabled, after Google Meet is generated on a scheduler run a
                  Connect online event is created with that Meet link, and meeting
                  invitees are registered as attendees. Unchecking stops future
                  automatic events; existing events are not deleted.
                </Form.Text>
                {createEvent && (
                  <div className="d-flex flex-column gap-2">
                    <Button
                      type="button"
                      variant="outline-primary"
                      className="align-self-start"
                      onClick={() => setShowEventConfig(true)}
                    >
                      Configure Event
                    </Button>
                    {eventTemplateJson ? (
                      <span className="small text-success">
                        Event template saved. You can reopen Configure Event to edit it.
                      </span>
                    ) : (
                      <span className="small text-warning">
                        Event template not configured yet.
                      </span>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Form>
        </Modal.Body>
        <Modal.Footer className="bg-white border-top">
          <Button variant="outline-secondary" onClick={closeConfig} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="d-inline-flex align-items-center gap-2 px-4 bg-project-gradient border-0"
          >
            {updateMutation.isPending ? (
              <>
                <Spinner animation="border" size="sm" />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <MeetingSchedulerEventConfigModal
        show={showEventConfig}
        membershipCategory={selected?.membershipCategory || "Forum"}
        initialJson={eventTemplateJson}
        saving={updateMutation.isPending}
        onHide={() => setShowEventConfig(false)}
        onSave={handleSaveEventTemplate}
      />

      <Modal
        show={guestsView != null}
        onHide={closeGuests}
        centered
        size="lg"
        backdrop="static"
        scrollable
        dialogClassName="meeting-scheduler-modal"
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fs-5 d-flex align-items-center gap-2">
            <BsPersonLinesFill size={20} />
            {selected?.membershipCategory || "Scheduler"} —{" "}
            {guestsView === "current" ? "Current meeting guests" : "Manage next meeting guests"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "60vh", overflowY: "auto" }}>
          {actionError && guestsView != null && (
            <div className="alert alert-danger py-2 mb-3">{actionError}</div>
          )}

          {guestsLoading && (
            <div className="d-flex align-items-center gap-2 text-muted py-4 justify-content-center">
              <Spinner animation="border" size="sm" />
              Loading guests…
            </div>
          )}

          {guestsIsError && (
            <div className="alert alert-danger py-2">
              {(guestsError as Error)?.message || "Failed to load guests."}
              <div className="mt-2">
                <Button size="sm" variant="outline-danger" onClick={() => refetchGuests()}>
                  Retry
                </Button>
              </div>
            </div>
          )}

          {!guestsLoading && !guestsIsError && guestsData && guestsView === "next" && (
            <div className="mb-4 pb-3 border-bottom">
              <div className="fw-semibold mb-2 d-flex align-items-center gap-2">
                <BsPeople size={16} />
                External attendees
              </div>
              <ExternalAttendeesField
                schedulerCategory={selected?.membershipCategory}
                emails={externalEmails}
                onChange={(emails) => {
                  void persistNextGuestSettings({ emails });
                }}
              />
              <div className="mt-3">
                <div className="fw-semibold small mb-2">Also include</div>
                {crossMembershipIncludeOptions(selected?.membershipCategory).map(
                  (opt) => {
                    const checked =
                      opt.key === "includeEarlyStageMembers"
                        ? includeEarlyStageMembers
                        : opt.key === "includeBusinessMembers"
                          ? includeBusinessMembers
                          : includeExecutiveMembers;
                    return (
                      <Form.Check
                        key={opt.key}
                        className="mb-2"
                        type="checkbox"
                        id={`${opt.key}-checkbox`}
                        label={opt.label}
                        checked={checked}
                        disabled={updateMutation.isPending}
                        onChange={(e) => {
                          void persistNextGuestSettings({
                            [opt.key]: e.target.checked,
                          });
                        }}
                      />
                    );
                  }
                )}
                <Form.Check
                  className="mb-1"
                  type="checkbox"
                  id="include-explorers-checkbox"
                  label="Include explorers (one invite per explorer, matched by segment)"
                  checked={includeExplorers}
                  disabled={updateMutation.isPending}
                  onChange={(e) => {
                    void persistNextGuestSettings({
                      includeExplorers: e.target.checked,
                    });
                  }}
                />
                <Form.Text className="text-muted d-block mt-1">
                  {explorerInviteSegmentHint(selected?.membershipCategory)}{" "}
                  Each explorer is invited only once across all forums.
                </Form.Text>
              </div>
            </div>
          )}

          {!guestsLoading && !guestsIsError && guestsData && (
            <InputGroup className="mb-3">
              <InputGroup.Text className="bg-white">
                <BsSearch className="text-muted" size={14} />
              </InputGroup.Text>
              <Form.Control
                type="search"
                placeholder="Search by name or email"
                value={guestsSearch}
                onChange={(e) => setGuestsSearch(e.target.value)}
              />
            </InputGroup>
          )}

          {!guestsLoading && !guestsIsError && guestsData && guestsView === "next" && (
            <>
              <p className="small text-muted mb-2">
                Who would be invited on the next scheduler run with current settings.
                Exclude someone to skip them until you include them again.
                {(guestsData.nextInvitees?.length ?? 0) > 0 && (
                  <>
                    {" "}
                    Showing {filteredNextInvitees.length} of {guestsData.nextInvitees.length}.
                  </>
                )}
              </p>
              {(guestsData.nextInvitees?.length ?? 0) === 0 ? (
                <div className="text-muted small mb-3">No invitees yet.</div>
              ) : filteredNextInvitees.length === 0 ? (
                <div className="text-muted small mb-3">No guests match your search.</div>
              ) : (
                <ListGroup variant="flush" className="border rounded mb-3">
                  {filteredNextInvitees.map((invitee) => {
                    const isOrganizer = invitee.kind === "Organizer";
                    const busy = guestActionBusyEmail?.toLowerCase() === invitee.email.toLowerCase();
                    return (
                      <ListGroup.Item
                        key={`${invitee.kind}-${invitee.email}`}
                        className="d-flex justify-content-between align-items-center gap-2"
                      >
                        <InviteeNameWithKindTag
                          fullName={invitee.fullName}
                          email={invitee.email}
                          kind={invitee.kind}
                        />
                        {!isOrganizer && (
                          <Button
                            size="sm"
                            variant="outline-danger"
                            className="flex-shrink-0"
                            disabled={busy}
                            onClick={() => handleExcludeGuest(invitee.email)}
                          >
                            {busy ? <Spinner animation="border" size="sm" /> : "Exclude"}
                          </Button>
                        )}
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              )}

              {(guestsData.excludedInvitees?.length ?? 0) > 0 && (
                <>
                  <div className="fw-semibold mb-2">
                    Excluded from next meeting{" "}
                    <span className="text-muted fw-normal">
                      ({filteredExcludedInvitees.length}
                      {guestsQuery ? ` of ${guestsData.excludedInvitees!.length}` : ""})
                    </span>
                  </div>
                  {filteredExcludedInvitees.length === 0 ? (
                    <div className="text-muted small">No excluded guests match your search.</div>
                  ) : (
                    <ListGroup variant="flush" className="border rounded">
                      {filteredExcludedInvitees.map((invitee) => {
                        const busy =
                          guestActionBusyEmail?.toLowerCase() === invitee.email.toLowerCase();
                        return (
                          <ListGroup.Item
                            key={`excluded-${invitee.kind}-${invitee.email}`}
                            className="d-flex justify-content-between align-items-center gap-2 bg-light"
                          >
                            <InviteeNameWithKindTag
                              fullName={invitee.fullName}
                              email={invitee.email}
                              kind={invitee.kind}
                              struck
                            />
                            <Button
                              size="sm"
                              variant="outline-success"
                              className="flex-shrink-0"
                              disabled={busy}
                              onClick={() => handleIncludeGuest(invitee.email)}
                            >
                              {busy ? <Spinner animation="border" size="sm" /> : "Include"}
                            </Button>
                          </ListGroup.Item>
                        );
                      })}
                    </ListGroup>
                  )}
                </>
              )}
            </>
          )}

          {!guestsLoading && !guestsIsError && guestsData && guestsView === "current" && (
            <>
              {!guestsData.currentMeeting ? (
                <div className="text-muted small">
                  No Meet has been created for this scheduler yet.
                </div>
              ) : (
                <>
                  {guestsData.currentMeeting.meetUrl && (
                    <div className="small mb-2">
                      <a
                        href={guestsData.currentMeeting.meetUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open Meet link
                      </a>
                    </div>
                  )}
                  {!guestsData.currentMeeting.canLoadGuests ? (
                    <div className="alert alert-info py-2 small mb-0">
                      {guestsData.currentMeeting.unavailableReason ||
                        "Current meeting guests will be available after the next Meet is created."}
                    </div>
                  ) : (guestsData.currentMeeting.guests?.length ?? 0) === 0 ? (
                    <div className="text-muted small">
                      No calendar guests found for this meeting.
                    </div>
                  ) : filteredCurrentGuests.length === 0 ? (
                    <div className="text-muted small">No guests match your search.</div>
                  ) : (
                    <>
                      {guestsQuery && (
                        <p className="small text-muted mb-2">
                          Showing {filteredCurrentGuests.length} of{" "}
                          {guestsData.currentMeeting.guests.length}.
                        </p>
                      )}
                      <ListGroup variant="flush" className="border rounded">
                        {filteredCurrentGuests.map((guest) => (
                          <ListGroup.Item
                            key={guest.email}
                            className="d-flex justify-content-between align-items-start gap-2"
                          >
                            <div className="min-w-0">
                              <div className="fw-semibold text-truncate">
                                {guest.displayName || guest.email}
                              </div>
                              <div className="small text-muted text-truncate">{guest.email}</div>
                            </div>
                            <span className="badge text-bg-light flex-shrink-0">
                              {formatGuestResponseStatus(guest.responseStatus)}
                            </span>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top">
          <Button variant="outline-secondary" onClick={closeGuests}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageMeetings;
